import React, { useMemo, useState } from 'react';
import { Modal } from '../../components/base';
import { STAGES } from '../../lib/constantes';
import { CAMPOS, parseCSV, prepararLote, sugerirMapeamento } from '../../lib/csv';

// Importação por CSV (roadmap #4): arquivo → mapeamento de colunas → prévia → gravar.
function ImportCSVModal({ leads = [], sellers = [], onImport, onClose }) {
  const [texto, setTexto] = useState('');
  const [mapa, setMapa] = useState(null);
  const [pularDuplicados, setPularDuplicados] = useState(true);
  const [progresso, setProgresso] = useState(null); // { feitos, total } | { pronto }

  const csv = useMemo(() => parseCSV(texto), [texto]);
  const mapaAtual = mapa || (csv.colunas.length ? sugerirMapeamento(csv.colunas) : {});
  const existentes = useMemo(() => new Set(leads.map(l => String(l.company_name || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim())), [leads]);
  const lote = useMemo(() => csv.linhas.length ? prepararLote(csv.linhas, mapaAtual, { sellers, existentes }) : null, [csv, mapaAtual, sellers, existentes]);
  const aImportar = lote ? lote.itens.filter(x => !x.erro && !(pularDuplicados && x.duplicado)) : [];

  const lerArquivo = (file) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = () => { setTexto(String(r.result || '')); setMapa(null); };
    r.readAsText(file, 'UTF-8');
  };

  const importar = async () => {
    setProgresso({ feitos: 0, total: aImportar.length });
    const ok = await onImport(aImportar.map(x => x.lead), (feitos) => setProgresso({ feitos, total: aImportar.length }));
    setProgresso({ pronto: ok });
  };

  const inp = 'apple-input px-2 py-1 text-xs';
  const etapa = (k) => STAGES.find(s => s.key === k)?.label || k;

  return <Modal title="Importar leads por CSV" onClose={onClose} wide>
    {progresso?.pronto !== undefined
      ? <div className="text-center py-8">
          <div className="text-4xl mb-2">✅</div>
          <p className="text-sm font-semibold text-gray-800">{progresso.pronto} lead{progresso.pronto === 1 ? '' : 's'} importado{progresso.pronto === 1 ? '' : 's'}</p>
          <button onClick={onClose} className="mt-4 bg-blue-600 text-white text-sm font-semibold px-5 py-2 rounded-full">Fechar</button>
        </div>
      : progresso
      ? <div className="text-center py-8">
          <p className="text-sm text-gray-600 mb-3">Gravando {progresso.feitos} de {progresso.total}…</p>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-blue-500 transition-all" style={{ width: `${progresso.total ? progresso.feitos / progresso.total * 100 : 0}%` }} /></div>
        </div>
      : <div className="space-y-4">
          {/* 1. Arquivo */}
          <div className="flex items-center gap-3 flex-wrap">
            <label className="bg-blue-50 text-blue-700 text-sm font-semibold px-4 py-2 rounded-full cursor-pointer hover:bg-blue-100">
              📂 Escolher arquivo .csv
              <input type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={e => lerArquivo(e.target.files?.[0])} />
            </label>
            <span className="text-xs text-gray-400">ou cole o conteúdo abaixo. Primeira linha = cabeçalho. Aceita ; , ou tab.</span>
          </div>
          <textarea className="apple-input w-full px-3 py-2 text-xs font-mono h-24" placeholder={'Empresa;Contato;Telefone;E-mail;Etapa;Valor\nACME;Maria;11 99999-0000;maria@acme.com;Em contato;1500'}
            value={texto} onChange={e => { setTexto(e.target.value); setMapa(null); }} />

          {/* 2. Mapeamento */}
          {csv.colunas.length > 0 && <div>
            <p className="text-xs font-semibold text-gray-500 mb-1.5">Colunas do arquivo → campos do lead</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {csv.colunas.map(col => {
                const campoAtual = Object.entries(mapaAtual).find(([, c]) => c === col)?.[0] || '';
                return <div key={col} className="flex items-center gap-1.5 min-w-0">
                  <span className="text-xs text-gray-700 truncate flex-1" title={col}>{col}</span>
                  <select className={inp} value={campoAtual} onChange={e => {
                    const novo = { ...mapaAtual };
                    Object.keys(novo).forEach(k => { if (novo[k] === col) delete novo[k]; });
                    if (e.target.value) novo[e.target.value] = col;
                    setMapa(novo);
                  }}>
                    <option value="">ignorar</option>
                    {CAMPOS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>;
              })}
            </div>
          </div>}

          {/* 3. Prévia */}
          {lote && <div>
            <div className="flex items-center gap-3 flex-wrap text-xs mb-2">
              <span className="font-semibold text-gray-700">{csv.linhas.length} linha{csv.linhas.length === 1 ? '' : 's'}</span>
              <span className="text-green-700">{lote.validos} válida{lote.validos === 1 ? '' : 's'}</span>
              {lote.erros > 0 && <span className="text-red-600">{lote.erros} sem empresa</span>}
              {lote.duplicados > 0 && <span className="text-amber-600">{lote.duplicados} já existe{lote.duplicados === 1 ? '' : 'm'}</span>}
              {lote.comAviso > 0 && <span className="text-gray-500">{lote.comAviso} com aviso</span>}
              {lote.duplicados > 0 && <label className="flex items-center gap-1.5 ml-auto text-gray-600 cursor-pointer">
                <input type="checkbox" checked={pularDuplicados} onChange={e => setPularDuplicados(e.target.checked)} /> pular as que já existem
              </label>}
            </div>
            {!mapaAtual.company_name && <p className="text-xs text-red-600 mb-2">Mapeie a coluna da Empresa para continuar.</p>}
            <div className="max-h-56 overflow-auto rounded-xl border border-black/[0.06]">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0"><tr>
                  <th className="px-2 py-1.5 text-left text-gray-400">#</th><th className="px-2 py-1.5 text-left text-gray-400">Empresa</th><th className="px-2 py-1.5 text-left text-gray-400">Contato</th><th className="px-2 py-1.5 text-left text-gray-400">Etapa</th><th className="px-2 py-1.5 text-right text-gray-400">Valor</th><th className="px-2 py-1.5 text-left text-gray-400">Situação</th>
                </tr></thead>
                <tbody>
                  {lote.itens.slice(0, 50).map(x => <tr key={x.n} className={`border-t border-black/[0.04] ${x.erro ? 'bg-red-50/60' : x.duplicado ? 'bg-amber-50/60' : ''}`}>
                    <td className="px-2 py-1 text-gray-400">{x.n}</td>
                    <td className="px-2 py-1 font-medium text-gray-800">{x.lead?.company_name || <span className="text-red-600">—</span>}</td>
                    <td className="px-2 py-1 text-gray-600">{x.lead?.contact_name || x.lead?.contact_email || x.lead?.contact_phone || ''}</td>
                    <td className="px-2 py-1 text-gray-600">{x.lead ? etapa(x.lead.stage) : ''}</td>
                    <td className="px-2 py-1 text-right text-gray-600">{x.lead?.value ? x.lead.value.toLocaleString('pt-BR') : ''}</td>
                    <td className="px-2 py-1 text-gray-500">{x.erro || (x.duplicado ? 'já existe' : x.avisos.join('; ') || 'ok')}</td>
                  </tr>)}
                </tbody>
              </table>
              {lote.itens.length > 50 && <p className="text-xs text-gray-400 px-2 py-1.5">… e mais {lote.itens.length - 50}</p>}
            </div>
          </div>}

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="text-sm text-gray-500 px-4 py-2 rounded-full hover:bg-gray-100">Cancelar</button>
            <button onClick={importar} disabled={!aImportar.length || !mapaAtual.company_name}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-5 py-2 rounded-full disabled:opacity-40">
              Importar {aImportar.length || ''}
            </button>
          </div>
        </div>}
  </Modal>;
}

export { ImportCSVModal };
